/**
 * Pure decision logic for the header organisation switcher, split out of `org-context.tsx` so
 * both rules are testable without a renderer — this repo runs none in its test process, by
 * design (`vitest.config.ts`'s file comment: "every one of the 39 suites is a plain `.ts` testing
 * pure logic that was split out of a `.tsx` precisely so it could be tested without a renderer").
 *
 * The REASONING for each rule — what was measured, which incident it closes — lives beside the
 * effect and the `visible` field in `org-context.tsx` and `org-context-value.ts`. This file keeps
 * only the decisions themselves, kept honest by the tests beside it.
 */

/**
 * Is the switcher worth offering?
 *
 * Two INDEPENDENT conditions, not a layered check — a caller who fails either one gets the same
 * hidden control, never a disabled one:
 *   · `canSwitch` — holds `tenants.edit`, the permission that gates the Organisation screen and
 *     every organisation edit. Deciding where a write lands is that same administrative act,
 *     wearing the header instead of a rail entry.
 *   · more than one organisation in reach — one organisation is not a choice to offer.
 */
export function orgSwitcherVisible(canSwitch: boolean, organisationCount: number): boolean {
  return canSwitch && organisationCount > 1;
}

/**
 * Where the live selection must be forced back to when it no longer belongs to this caller, or
 * `null` when nothing needs to move.
 *
 * This exists for exactly one case: `canSwitch` has gone false while the selection is sitting
 * somewhere other than home. It deliberately does NOT fire for the other half of
 * `orgSwitcherVisible` — a reach that has shrunk to one organisation — because the server's own
 * fallback already guarantees the selection is home in that case (an id outside the reach is
 * ignored), so there is nothing left for the client to correct.
 *
 * The permission half has no such guarantee: `manageableOrgIds` (lumen-be) resolves reach from
 * the organisation hierarchy alone and carries no permission check, because ordinary reads are
 * meant to widen for everybody regardless of role. So a caller who loses `tenants.edit` while
 * sitting in a descendant is NOT made unreachable by that loss — the server keeps honouring the
 * old selection — and the client has to make the "an unreachable choice falls back to home" call
 * itself, or leave the caller stranded exactly as `78bd790` was written to prevent.
 *
 * `loading` is a third, simpler guard: before the first answer arrives, `selectedId` and `homeId`
 * are both the empty-string placeholder and agree trivially, but nothing should act on data it
 * does not have yet regardless of what the other two happen to evaluate to.
 */
export function orgSwitchStrandTarget(params: {
  loading: boolean;
  canSwitch: boolean;
  selectedId: string;
  homeId: string;
}): string | null {
  const { loading, canSwitch, selectedId, homeId } = params;
  if (loading || canSwitch || selectedId === homeId) return null;
  return homeId;
}
